import React, { ChangeEvent, useContext, useMemo } from 'react';

import PropTypes from 'prop-types';

import { sva } from '@adsk/alloy-react-helpers';
import { useTheme } from '@adsk/alloy-react-styles';
import { stylePropType, StylableComponent } from '@adsk/alloy-react-theme';

import RadioGroupContext from './RadioGroupContext';

type RadioButtonProps = Omit<StylableComponent<HTMLDivElement>, 'onChange'> & {
  name?: string;
  checked?: boolean;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string | undefined, e: ChangeEvent) => void;
  inputProps?: StylableComponent<HTMLInputElement>;
  'data-testid'?: string;
};
/**
 * Provides a list of choices (radio buttons) within a form when only one item may be selected at a time.
 */
const RadioButton = React.forwardRef<HTMLInputElement, RadioButtonProps>(
  (
    {
      style,
      className,
      id,
      name: nameProp,
      checked: checkedProp,
      disabled = false,
      value,
      onChange: onChangeProp,
      inputProps = {},
      'data-testid': dataTestid,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const containerTokens = theme.tokens?.component?.radioButton?.container;
    const shadowTokens = theme.tokens?.component?.radioButton?.shadow;
    const inputTokens = theme.tokens?.component?.radioButton?.input;

    const radioGroup = useContext(RadioGroupContext);
    let checked = checkedProp;
    let name = nameProp;

    if (radioGroup) {
      if (typeof checked === 'undefined') {
        checked = radioGroup.value === value;
      }
      if (typeof name === 'undefined') {
        name = radioGroup.name;
      }
    }

    const handleChange = (e: ChangeEvent) => {
      if (onChangeProp) {
        onChangeProp(value, e);
      } else if (radioGroup) {
        radioGroup.onChange?.(value, e);
      }
    };

    const radioStyles = useMemo(
      () =>
        sva(containerTokens?._, {
          variants: {
            checked: containerTokens?.checked,
            disabled: containerTokens?.disabled,
          },
          compoundVariants: containerTokens?.compoundVariants,
        }),
      [containerTokens],
    );

    const shadowStyles = useMemo(
      () =>
        shadowTokens
          ? sva(shadowTokens._, {
              variants: {
                checked: shadowTokens.checked,
                disabled: shadowTokens.disabled,
              },
              compoundVariants: shadowTokens.compoundVariants,
            })
          : undefined,
      [shadowTokens],
    );

    const inputStyles = useMemo(
      () =>
        sva(inputTokens?._, {
          variants: {
            checked: inputTokens?.checked,
            disabled: inputTokens?.disabled,
          },
        }),
      [inputTokens],
    );

    const { style: inputStyle, ...rest } = inputProps;

    return (
      <div
        className={className}
        css={[radioStyles({ checked, disabled }), style]}
        {...props}
      >
        {shadowStyles && <div css={shadowStyles({ checked, disabled })} />}
        <input
          ref={ref}
          id={id}
          name={name}
          type="radio"
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={disabled ? undefined : handleChange}
          data-testid={dataTestid}
          css={[inputStyles({ checked, disabled }), inputStyle]}
          {...rest}
        />
      </div>
    );
  },
);

RadioButton.displayName = 'RadioButton';

RadioButton.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The id of the radio, used for the label */
  id: PropTypes.string,
  /** The name for the radio input */
  name: PropTypes.string,
  /** The value of the radio */
  checked: PropTypes.bool,
  /** The value of the radio */
  value: PropTypes.any,
  /** onChange handler for the radio input */
  onChange: PropTypes.func,
  /** Disable radio functionality */
  disabled: PropTypes.bool,
  /** Props passed to the input element */
  inputProps: PropTypes.object,
  /** data-testid passed to the input element */
  'data-testid': PropTypes.string,
};

export default RadioButton;
